'use client'

import { useEffect } from 'react'
import posthog from 'posthog-js'
import {
  ATTRIBUTION_COOKIE,
  CONSENT_COOKIE,
  applyAnalyticsConsent,
  attributionPersonProperties,
  decodeAttributionCookie,
  parseConsentCookie,
  readCookieValue,
} from '@/lib/analytics/attribution'

// MOD-97: identify the signed-in user so PostHog error events and product
// analytics link to a person. Mounted in the (app) layout, which already
// resolves the user on the server — we hand the resolved fields down to
// avoid a duplicate Supabase call from the client.
//
// BAO-125: identify on the FIRST authed render, not once a family exists.
// `posthog.identify` is the only call that merges the anonymous marketing
// visit (UTMs, landing page, `$initial_*`) into this person; gating it on
// family+role meant a signup that never created a family - most drop-offs -
// never linked back to the visit that produced it. Family and role arrive
// as `$set` person properties once known, so there is never a second
// identify carrying nulls.
//
// Impersonation (platform admin viewing as a family, `bh_impersonating`
// cookie): identify is skipped entirely. posthog-js switches distinct_id
// without merging on a second identify, so identifying the impersonated user
// would file the admin's pageviews under a real customer's person - one that
// carries no `is_internal` and passes the project filter.
//
// BAO-125 Phase 2: the `bh_attr` cookie rides along with identify as
// `$set_once` (first touch → `first_utm_source`, ...) and `$set` (last touch).
// Doing it here, not in the capture component, means an anonymous marketing
// visitor never gets a person profile created just to hold these. Nothing is
// attached when analytics consent is denied, and the families route clears
// the cookie once a family is created so a later account on the same browser
// cannot inherit it.
export function PostHogIdentify({
  userId,
  familyId,
  role,
  impersonating = false,
}: {
  userId: string
  familyId: string | null
  role: string | null
  impersonating?: boolean
}) {
  useEffect(() => {
    if (!process.env.NEXT_PUBLIC_POSTHOG_KEY) return
    if (!userId || impersonating) return
    const attribution = readAttributionCookie()
    if (attribution) {
      const props = attributionPersonProperties(attribution)
      posthog.identify(userId, props.set, props.setOnce)
    } else {
      posthog.identify(userId)
    }
    // No reset on cleanup; reset belongs in sign-out so session switches
    // don't accidentally bleed events to the wrong distinct id.
  }, [userId, impersonating])

  useEffect(() => {
    if (!process.env.NEXT_PUBLIC_POSTHOG_KEY) return
    if (!userId || impersonating || !familyId || !role) return
    posthog.setPersonProperties({ family_id: familyId, role })
  }, [userId, familyId, role, impersonating])

  return null
}

function readAttributionCookie() {
  try {
    if (typeof document === 'undefined') return null
    const consent = parseConsentCookie(readCookieValue(document.cookie, CONSENT_COOKIE))
    return applyAnalyticsConsent(
      decodeAttributionCookie(readCookieValue(document.cookie, ATTRIBUTION_COOKIE)),
      consent,
    )
  } catch {
    return null
  }
}
